// Client
import Client from 'fhir-kit-client';
// React
import { FunctionComponent, useCallback, useRef, useState } from "react";
// Component
import PandoraPage from '../../components/PandoraPage/PandoraPage';
import OperationsSection from 'src/pages/TestEditor/OperationsSection/OperationsSection';
import GeneralInformationSection from './GeneralInformation/GeneralInformation';
import AbstractServerSection from './AbstractServerSection/AbstractServerSection';
import ProfilesSection, { Validation } from './ProfilesSection/ProfilesSection';
import FixturesSection, { Fixture } from './FixturesSection/FixturesSection';
import VariablesSection, { Variable } from './VariablesSection/VariablesSection';
import validatorProfilesMap from './ProfilesSection/validatorProfilesMap';
import { Form, Button } from 'react-bootstrap';
import operationTypeMap from './OperationsSection/OperationTypeMap';
// Translation
import i18n from 'i18next';
import { useOnMount } from "src/hooks/useOnMount";
// React Router Dom
import { useNavigate, useParams } from 'react-router-dom';
// Dayjs
import dayjs from 'dayjs';
// Fhir
import { DocumentReference, TestScript, FhirResource, TestScriptSetupActionAssert, TestScriptSetupActionOperation } from 'fhir/r5';
// Buffer
import { Buffer } from 'buffer';

const TestEditor: FunctionComponent = () => {

    /////////////////////////////////////
    //             States              //
    /////////////////////////////////////

    const { testId } = useParams();

    // Loader to display the loading spinner
    const [loading, setLoading] = useState(false);
    // If true, the form will be styled with the validation of inputs
    const [validated, setValidated] = useState(false);

    // General Informations
    const [generalInformations, setGeneralInformations] = useState({
        id: '',
        automatable: 'not-automatable',
        url: '',
        version: '',
        name: '',
        title: '',
        status: '',
        //TODO This will be to see, espacially giving the fact that we have edition coming up next
        date: dayjs().format('YYYY-MM-DD'),
        description: ''
    });
    const [isNew, setIsNew] = useState(!testId);

    // Origins 
    const [origins, setOrigins] = useState([] as { id: number, profile: string, url: string }[]);

    // Destinations
    const [destinations, setDestinations] = useState([] as { id: number, profile: string, url: string }[]);

    // Fixtures
    const [fixtures, setFixtures] = useState<Fixture[]>([]);

    // Variables
    const [variables, setVariables] = useState<Variable[]>([]);

    // The profiles validation
    const [profilesValidation, setProfilesValidation] = useState<Validation[]>([]);

    // Operations
    const [operationFormData, setOperationFormData] = useState<any>({});

    // Asserts
    const [assertFormData, setAssertFormData] = useState<any>({});

    /////////////////////////////////////
    //             Init                //
    /////////////////////////////////////

    /**
     * Load the test script if the testId is present
     * 
     */
    useOnMount(() => {
        if (!testId) {
            return;
        }
        void loadTestScript();
    });

    /**
     * Retrieve the validator and the profile name from the profile URL
     * 
     * @param profileUrl 
     *  
     */
    function findValidatorAndProfileByUrl(profileUrl: string) {
        const validatorId = Object.keys(validatorProfilesMap).find(validatorId => {
            return validatorProfilesMap[validatorId].some(profile => profile.url === profileUrl);
        });
        const profileName = validatorId
            ? validatorProfilesMap[validatorId].find(profile => profile.url === profileUrl)?.name
            : '';
        return { validatorId: validatorId ?? '', profileName: profileName ?? '' };
    }

    /**
     * Load the test script for the edition
     * 
     */
    const loadTestScript = async () => {
        setLoading(true);
        try {
            const response = await fhirClient.read({
                resourceType: 'TestScript',
                id: testId ?? ''
            })
            const results: TestScript = response as TestScript;
            // TODO Map this into the correct data for the edition page.
            setGeneralInformations(
                {
                    id: response.id,
                    // TODO Retrieve the tag
                    automatable: 'not-automatable',
                    url: response.url,
                    version: response.version,
                    name: response.name,
                    title: response.title,
                    status: response.status,
                    date: dayjs().format('YYYY-MM-DD'),
                    description: response.description
                }
            );
            setIsNew(false);
            // TODO This uses the index as an ID, delete is broken ...
            setOrigins(
                results.origin?.map((origin, index) => {
                    return {
                        id: index,
                        profile: origin.profile.code ?? '',
                        url: origin.url ?? ''
                    }
                }
                ) ?? []
            );
            setDestinations(
                results.destination?.map((destination, index) => {
                    return {
                        id: index,
                        profile: destination.profile.code ?? '',
                        url: destination.url ?? ''
                    }
                }
                ) ?? []
            );
            setVariables(
                results.variable?.map((variable, index) => {
                    return {
                        internalId: variable.name ?? 'variable-' + index,
                        name: variable.name ?? '',
                        sourceId: variable.sourceId ?? '',
                        expression: variable.expression ?? '',
                        path: variable.path ?? '',
                    }
                }
                ) ?? []
            );
            const profiles = results.profile?.map((profile, index) => {
                const { validatorId, profileName } = findValidatorAndProfileByUrl(profile);
                return {
                    internalId: `validation-${index}`,
                    id: index,
                    validator: validatorId ?? '',
                    profileName: profileName ?? '',
                    profileUrl: profile
                };
            }) ?? [];
            setProfilesValidation(profiles);
            if (results.fixture) {
                const documents = [] as { id: string, documentReference: DocumentReference }[];
                for (const resultFixture of results.fixture) {
                    const documentReference = await fhirClient.read({
                        resourceType: 'DocumentReference',
                        id: resultFixture.resource?.reference?.split('/')[1] ?? ''
                    })
                    documents.push({ id: resultFixture.id ?? '', documentReference: documentReference as DocumentReference });
                }
                setFixtures(
                    results.fixture?.map((fixture, index) => {
                        // TODO Actually retrieve DocumentReferences for content to display
                        return {
                            internalId: fixture.id ?? 'fixture-' + index,
                            id: fixture.id ?? '',
                            resource: getDocumentContent(documents.find(doc => doc.id === fixture.id)?.documentReference),
                            autocreate: fixture.autocreate,
                            autodelete: fixture.autodelete,
                        }
                    }
                    ) ?? []
                );
            }
            var operationForms = [];
            var operationFormDataFromTestScript = {};
            var assertForms = [];
            var assertFormsPerOperation = {};
            var currentOperation = undefined;
            for (const setupAction of results.setup?.action ?? []) {
                if (setupAction.operation) {
                    if (currentOperation !== undefined) {
                        assertFormsPerOperation = { ...assertFormsPerOperation, [currentOperation.id ?? '']: assertForms };
                        assertForms = [];
                    }
                    currentOperation = setupAction.operation;
                    operationForms.push({
                        id: currentOperation.id ?? '',
                        description: currentOperation.description ?? '',
                        operationType: currentOperation.type?.code
                            ? operationTypeMap[currentOperation.type.code] || 'rest-operation'
                            : 'free-operation',
                        type: currentOperation.type?.code && currentOperation.type?.code !== 'custom-operation'
                            ? { system: currentOperation.type?.system ?? '', code: 'custom-operation' }
                            : currentOperation.type ?? { system: '', code: '' },
                        customSystem: currentOperation.type?.code && currentOperation.type?.code !== 'custom-operation'
                            ? currentOperation.type.system
                            : '',
                        customCode: currentOperation.type?.code && currentOperation.type?.code !== 'custom-operation'
                            ? currentOperation.type.code
                            : '',
                        method: currentOperation.method ?? '',
                        label: currentOperation.label ?? '',
                        resource: currentOperation.resource ?? '',
                        returnResourceType: currentOperation.extension?.find(e => e.url === "http://isis.com/test/StructureDefinition/EXT-ReturnResourceType")?.valueString ?? '',
                        accept: 'application/fhir+json',
                        contentType: currentOperation.contentType ?? '',
                        // TODO See for originin and destination
                        origin: currentOperation.origin ?? '',
                        destination: currentOperation.destination ?? '',
                        // TODO See if this is working ?
                        sourceId: currentOperation.sourceId ?? '',
                        targetId: currentOperation.targetId ?? '',
                        responseId: currentOperation.responseId ?? '',
                        url: currentOperation.url ?? '',
                        params: currentOperation.params ?? '',
                        encodeRequestUrl: currentOperation.encodeRequestUrl
                    })
                } else if (setupAction.assert && currentOperation !== undefined) {
                    const currentAssert = setupAction.assert;
                    assertForms.push({
                        id: currentAssert.id ?? '',
                        description: currentAssert.description ?? '',
                        label: currentAssert.label ?? '',
                        direction: currentAssert.direction ?? '',
                        expression: currentAssert.expression ?? '',
                        responseCode: currentAssert.responseCode ?? '',
                        // TODO Find the internal ID for the profile
                        validateProfileId: profiles.find(profile => profile.id.toString() === currentAssert.validateProfileId)?.internalId ?? '',
                        headerField: currentAssert.headerField ?? '',
                        // TODO 
                        valueToCompare: '' as string,
                        operator: currentAssert.operator ?? '',
                        requestMethod: currentAssert.requestMethod ?? '',
                        requestUrl: currentAssert.requestURL ?? '',
                        contentType: currentAssert.contentType ?? '',
                        resource: currentAssert.resource ?? '',
                        sourceId: currentAssert.sourceId ?? '',
                        stopTestOnFail: currentAssert.stopTestOnFail,
                        warningOnly: currentAssert.warningOnly
                    });
                }
            }
            if (currentOperation !== undefined) {
                assertFormsPerOperation = { ...assertFormsPerOperation, [currentOperation.id ?? '']: assertForms };
                assertForms = [];
            }
            operationFormDataFromTestScript = { "setup": operationForms };
            operationForms = [];
            currentOperation = undefined;

            for (const testAction of results.test ? results.test[0].action : []) {
                if (testAction.operation) {
                    if (currentOperation !== undefined) {
                        assertFormsPerOperation = { ...assertFormsPerOperation, [currentOperation.id ?? '']: assertForms };
                        assertForms = [];
                    }
                    currentOperation = testAction.operation;
                    operationForms.push({
                        id: currentOperation.id ?? '',
                        description: currentOperation.description ?? '',
                        operationType: currentOperation.type?.code
                            ? operationTypeMap[currentOperation.type.code] || 'rest-operation'
                            : 'free-operation',
                        type: currentOperation.type?.code && currentOperation.type?.code !== 'custom-operation'
                            ? { system: currentOperation.type?.system ?? '', code: 'custom-operation' }
                            : currentOperation.type ?? { system: '', code: '' },
                        customSystem: currentOperation.type?.code && currentOperation.type?.code !== 'custom-operation'
                            ? currentOperation.type.system
                            : '',
                        customCode: currentOperation.type?.code && currentOperation.type?.code !== 'custom-operation'
                            ? currentOperation.type.code
                            : '',
                        method: currentOperation.method ?? '',
                        label: currentOperation.label ?? '',
                        resource: currentOperation.resource ?? '',
                        returnResourceType: currentOperation.extension?.find(e => e.url === "http://isis.com/test/StructureDefinition/EXT-ReturnResourceType")?.valueString ?? '',
                        accept: 'application/fhir+json',
                        contentType: currentOperation.contentType ?? '',
                        // TODO See for originin and destination
                        origin: currentOperation.origin ?? '',
                        destination: currentOperation.destination ?? '',
                        // TODO See if this is working ?
                        sourceId: currentOperation.sourceId ?? '',
                        targetId: currentOperation.targetId ?? '',
                        responseId: currentOperation.responseId ?? '',
                        url: currentOperation.url ?? '',
                        params: currentOperation.params ?? '',
                        encodeRequestUrl: currentOperation.encodeRequestUrl
                    })
                } else if (testAction.assert && currentOperation !== undefined) {
                    const currentAssert = testAction.assert;
                    assertForms.push({
                        id: currentAssert.id ?? '',
                        description: currentAssert.description ?? '',
                        label: currentAssert.label ?? '',
                        direction: currentAssert.direction ?? '',
                        expression: currentAssert.expression ?? '',
                        responseCode: currentAssert.responseCode ?? '',
                        // TODO Find the internal ID for the profile
                        validateProfileId: profiles.find(profile => profile.id.toString() === currentAssert.validateProfileId)?.internalId ?? '',
                        headerField: currentAssert.headerField ?? '',
                        // TODO
                        valueToCompare: '' as string,
                        operator: currentAssert.operator ?? '',
                        requestMethod: currentAssert.requestMethod ?? '',
                        requestUrl: currentAssert.requestURL ?? '',
                        contentType: currentAssert.contentType ?? '',
                        resource: currentAssert.resource ?? '',
                        sourceId: currentAssert.sourceId ?? '',
                        stopTestOnFail: currentAssert.stopTestOnFail,
                        warningOnly: currentAssert.warningOnly
                    });
                }
            }
            if (currentOperation !== undefined) {
                assertFormsPerOperation = { ...assertFormsPerOperation, [currentOperation.id ?? '']: assertForms };
                assertForms = [];
            }
            operationFormDataFromTestScript = { ...operationFormDataFromTestScript, "test": operationForms };
            operationForms = [];
            currentOperation = undefined;

            for (const teardownAction of results.teardown?.action ?? []) {
                if (teardownAction.operation) {
                    if (currentOperation !== undefined) {
                        assertFormsPerOperation = { ...assertFormsPerOperation, [currentOperation.id ?? '']: assertForms };
                        assertForms = [];
                    }
                    currentOperation = teardownAction.operation;
                    operationForms.push({
                        id: currentOperation.id ?? '',
                        description: currentOperation.description ?? '',
                        operationType: currentOperation.type?.code
                            ? operationTypeMap[currentOperation.type.code] || 'rest-operation'
                            : 'free-operation',
                        type: currentOperation.type?.code && currentOperation.type?.code !== 'custom-operation'
                            ? { system: currentOperation.type?.system ?? '', code: 'custom-operation' }
                            : currentOperation.type ?? { system: '', code: '' },
                        customSystem: currentOperation.type?.code && currentOperation.type?.code !== 'custom-operation'
                            ? currentOperation.type.system
                            : '',
                        customCode: currentOperation.type?.code && currentOperation.type?.code !== 'custom-operation'
                            ? currentOperation.type.code
                            : '',
                        method: currentOperation.method ?? '',
                        label: currentOperation.label ?? '',
                        resource: currentOperation.resource ?? '',
                        returnResourceType: currentOperation.extension?.find(e => e.url === "http://isis.com/test/StructureDefinition/EXT-ReturnResourceType")?.valueString ?? '',
                        accept: 'application/fhir+json',
                        contentType: currentOperation.contentType ?? '',
                        // TODO See for origin and destination
                        origin: currentOperation.origin ?? '',
                        destination: currentOperation.destination ?? '',
                        // TODO See if this is working ?
                        sourceId: currentOperation.sourceId ?? '',
                        targetId: currentOperation.targetId ?? '',
                        responseId: currentOperation.responseId ?? '',
                        url: currentOperation.url ?? '',
                        params: currentOperation.params ?? '',
                        encodeRequestUrl: currentOperation.encodeRequestUrl
                    })
                }
            }
            operationFormDataFromTestScript = { ...operationFormDataFromTestScript, "teardown": operationForms };
            setAssertFormData(assertFormsPerOperation);
            setOperationFormData(operationFormDataFromTestScript);
            setLoading(false);
        } catch (error) {
            alert("Could not retrieve test !");
            setLoading(false);
        }
    }

    /**
     * Get the content of a document reference and convert it to a base64 string
     * 
     * @param documentReference 
     * 
     */
    const getDocumentContent = (documentReference: DocumentReference | undefined): string => {
        // TODO that is probably not normal
        if (!documentReference) {
            return '';
        }
        return Buffer.from((documentReference as DocumentReference).content
            .map(c => c.attachment.data).find(d => d) ?? '', 'base64').toString();
    }

    /////////////////////////////////////
    //             Refs                //
    /////////////////////////////////////

    const generalInformationsRef = useRef<HTMLDivElement>(null);
    const originRef = useRef<HTMLDivElement>(null);
    const destinationRef = useRef<HTMLDivElement>(null);

    /////////////////////////////////////
    //             Options             //
    /////////////////////////////////////

    // Options for the origin select
    const originOptions = [
        { code: '', display: i18n.t('placeholder.origin') },
        { code: 'FHIR-Client', display: 'FHIR Client' },
        { code: 'FHIR-SDC-FormFiller', display: 'FHIR SDC FormFiller' }
    ];

    // Options for the destination select
    const destinationOptions = [
        { code: '', display: i18n.t('placeholder.destination') },
        { code: 'FHIR-Server', display: 'FHIR Server' },
        { code: 'FHIR-SDC-FormManager', display: 'FHIR SDC FormManager' },
        { code: 'FHIR-SDC-FormProcessor', display: 'FHIR SDC FormProcessor' },
        { code: 'FHIR-SDC-FormReceiver', display: 'FHIR SDC FormReceiver' }
    ];

    /////////////////////////////////////
    //             Client              //
    /////////////////////////////////////

    const fhirClient = new Client({
        baseUrl: process.env.REACT_APP_FHIR_URL ?? 'fhir'
    });

    /////////////////////////////////
    //        NAVIGATION           //
    /////////////////////////////////

    const navigate = useNavigate();

    const onError = useCallback((error?: unknown) => {
        navigate("/Error", { state: { error } });
    }, [navigate]);

    const onTestScriptViewer = useCallback((id: string) => {
        navigate("/TestScriptViewer/" + id);
    }, [navigate]);

    /////////////////////////////////////
    //             Actions             //
    /////////////////////////////////////

    // GENERAL INFORMATIONS

    /**
     * Check if the test script ID is unique on the server
     * 
     * @param testScriptId 
     * 
     * @returns a boolean
     * 
     */
    const isTestScriptIdUniqueOnServer = async (testScriptId: string): Promise<boolean> => {
        try {
            const response = await fhirClient.read({ resourceType: 'TestScript', id: testScriptId });
            if (response) {
                return false;
            }
        } catch (error) {
            return true;
        }
        return true;
    };

    /**
    * Check if the general informations are valid
    * 
    */
    const checkGeneralInformationsValidity = () => {
        if (!generalInformations.status || generalInformations.status === i18n.t('placeholder.choosestatus')) {
            generalInformationsRef.current?.scrollIntoView({ behavior: 'smooth' });
            return false;
        }
        if (!generalInformations.id || !/^[A-Za-z0-9.-]{1,64}$/.test(generalInformations.id)) {
            generalInformationsRef.current?.scrollIntoView({ behavior: 'smooth' });
            window.alert(i18n.t('errormessage.invalidformatid'));
            return false;
        }
        if (!generalInformations.id || !generalInformations.title || !generalInformations.url || !generalInformations.version || !generalInformations.description) {
            generalInformationsRef.current?.scrollIntoView({ behavior: 'smooth' });
            return false;
        }
        return true;
    };

    /**
     * Change the value of a field in the general informations
     * 
     * @param name 
     * @param value 
     */
    const changeValueInGeneralInformation = (name: string, value: string) => {
        setGeneralInformations(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    // ORIGIN AND DESTINATION

    /**
     * Check if a server profile is used in any operation
     * 
     * @param profileId 
     * 
     */
    const isServerProfileUsed = (profileId: string): boolean => {
        return isServerProfileUsedInOperations(profileId, operationFormData.setup, origins, destinations) ||
            isServerProfileUsedInOperations(profileId, operationFormData.test, origins, destinations) ||
            isServerProfileUsedInOperations(profileId, operationFormData.teardown, origins, destinations);
    };

    /**
     * Check if an origin or a destination is used in an operation
     * 
     * @param profileId 
     * @param operations 
     * 
     */
    const isServerProfileUsedInOperations = (profileId: string, operations: any[], origins: { profile: string }[], destinations: { profile: string }[]) => {
        if (!operations) {
            return false;
        }
        const isUsed = operations.some(operation => {
            const originProfile = origins[Number(operation.origin) - 1]?.profile;
            const destinationProfile = destinations[Number(operation.destination) - 1]?.profile;
            return originProfile === profileId || destinationProfile === profileId;
        });
        return isUsed;
    };

    /**
     * Have at least one origin and one destination before validating the form
     * 
     */
    const checkOriginAndDestinationValidity = () => {
        if (origins.length === 0 || !origins.some(origin => origin.profile !== '')) {
            originRef.current?.scrollIntoView({ behavior: 'smooth' });
            return false;
        }
        if (destinations.length === 0 || !destinations.some(destination => destination.profile !== '')) {
            destinationRef.current?.scrollIntoView({ behavior: 'smooth' });
            return false;
        }
        return true;
    };

    // VALIDATION ACTIONS

    /**
     * Check if a profile validation is used in an assert
     * 
     */
    const isProfileUsed = (profileId: string) => {
        return isProfileUsedInAsserts(profileId);
    };

    /**
     * Check if a profile validation is used in an assert
     * 
     * @param profileId 
     * 
     */
    const isProfileUsedInAsserts = (profileId: string) => {
        return Object.values(assertFormData).some(asserts => (
            asserts as any[]).some((assert: any) =>
                assert.validateProfileId === profileId)
        );
    };

    // FIXTURES

    /**
     * Check if a fixture is used in any operation 
     * 
     */
    const isFixtureUsed = (fixtureId: string) => {
        return isFixtureUsedInOperations(fixtureId, operationFormData.setup)
            || isFixtureUsedInOperations(fixtureId, operationFormData.test) ||
            isFixtureUsedInOperations(fixtureId, operationFormData.teardown) ||
            isFixtureUsedInVariables(fixtureId) ||
            isFixtureUsedInAsserts(fixtureId);
    };

    /**
     * Check if a fixture is used in an operation 
     * 
     */
    const isFixtureUsedInOperations = (fixtureId: string, operations: any[]) => {
        if (!operations) {
            return false;
        }
        // TODO use the internal ID here !!
        return operations.some(operation => operation.targetId === fixtureId || operation.sourceId === fixtureId || operation.responseId === fixtureId);
    };

    /**
     * Check if a fixture is used in any assert
     * 
     * @param fixtureId 
     * @returns 
     */
    const isFixtureUsedInAsserts = (fixtureId: string) => {
        // TODO assertFormData should not be of any type !!!
        return Object.values(assertFormData).some(asserts => (asserts as any[]).some((assert: any) => assert.sourceId === fixtureId));
    };

    /**
     * Check if a fixture is used in any variable
     * 
     * @param fixtureId 
     *  
     */
    const isFixtureUsedInVariables = (fixtureId: string) => {
        return variables.some(variable => variable.sourceId === fixtureId);
    };

    /**
     * Handle the encoding of the fixture data
     * 
     * @param fixtureContent the data to be encoded
     * @returns an encoded data
     */
    const handleFixtureDataEncode = (fixtureContent: string) => {
        const encodedData = Buffer.from(fixtureContent).toString('base64');
        if (fixtureContent) {
            return encodedData;
        } else {
            onError();
        }
    };

    // VARIABLES

    /**
     * Check if a variable is used in any operation 
     * 
     */
    const isVariableUsed = (variableId: string) => {
        return isVariableUsedInOperation(variableId, operationFormData.setup)
            || isVariableUsedInOperation(variableId, operationFormData.test) ||
            isVariableUsedInOperation(variableId, operationFormData.teardown);
    };

    /**
    * Check if a variable is used in an operation
    * 
    */
    const isVariableUsedInOperation = (variableId: string, operations: any[]) => {
        if (!operations) {
            return false;
        }
        return operations.some(operation => operation.targetId === variableId);
    };

    // PROFILES VALIDATION

    /**
     * Combine the profiles validation id and the profile name
     * 
     */
    const combinedProfiles = profilesValidation.map(validation => {
        const displayName = validation.profileName + ' (' + validation.profileUrl + ')'
        return {
            internalId: validation.internalId,
            name: displayName
        };
    });

    // OPERATIONS AND ASSERTS

    /**
     * Handle the change of the operation and assert fields
     * 
     * @param operations 
     * @param asserts 
     * 
     */
    const combineFormData = (operations: any, asserts: any) => {
        const result: {}[] = [];
        if (operations) {
            operations.forEach((operation: any) => {
                if ((operation && operation.type && operation.type.system && operation.type.code) || operation.operationType === 'free-operation') {
                    result.push({ "operation": transformOperationToFHIR(operation) });
                    if (asserts[operation.id]) {
                        asserts[operation.id].forEach((assert: any) => {
                            if (assert && assert.id && assert.label) {
                                result.push({ "assert": transformAssertToFHIR(assert) });
                            }
                        });
                    }
                }
            });
        }
        if (result.length === 0) {
            return undefined;
        }
        return { action: result };
    };

    /**
     * Transform the operation to a FHIR object
     * 
     * @param operation 
     * @returns 
     */
    const transformOperationToFHIR = (operation: any) => {
        const fhirOperation = {
            id: operation.id,
            description: operation.description,
            label: operation.label,
            type: operation.type,
            encodeRequestUrl: operation.encodeRequestUrl
        } as TestScriptSetupActionOperation;
        if (operation.operationType && operation.operationType !== '' && operation.operationType !== 'free-operation') {
            fhirOperation.type = {
                system: 'http://hl7.org/fhir/restful-interaction',
                code: operation.type.code
            };
        }
        // TODO see if this is really needed ? Because is not in the profile
        // if (operation.operationType === 'free-operation') {
        //     fhirOperation.type = {
        //         system: 'http://custom-system.com/free-operation',
        //         code: 'free-operation'
        //     };
        // }
        if (operation.customCode && operation.customCode !== '') {
            fhirOperation.type = {
                system: operation.customSystem,
                code: operation.customCode
            };
        }
        if (operation.returnResourceType && operation.returnResourceType !== '') {
            fhirOperation.extension = [];
            fhirOperation.extension.push({
                url: "http://isis.com/test/StructureDefinition/EXT-ReturnResourceType",
                valueString: operation.returnResourceType
            })
        }
        if (operation.method && operation.method !== '') {
            fhirOperation.method = operation.method;
        }
        if (operation.resource && operation.resource !== '') {
            fhirOperation.resource = operation.resource;
        }
        if (operation.accept && operation.accept !== '') {
            fhirOperation.accept = operation.accept;
        }
        if (operation.contentType && operation.contentType !== '') {
            fhirOperation.contentType = operation.contentType;
        }
        if (operation.origin && operation.origin !== '') {
            fhirOperation.origin = +operation.origin;
        }
        if (operation.destination && operation.destination !== '') {
            fhirOperation.destination = +operation.destination;
        }
        if (operation.sourceId && operation.sourceId !== '') {
            fhirOperation.sourceId = fixtures.find(fixture => fixture.internalId === operation.sourceId)?.id;
        }
        if (operation.targetId && operation.targetId !== '') {
            fhirOperation.targetId = fixtures.find(fixture => fixture.internalId === operation.targetId)?.id ??
                variables.find(variable => variable.internalId === operation.targetId)?.name ?? operation.targetId;
        }
        // TODO See with asserts ? 
        if (operation.responseId && operation.responseId !== '') {
            fhirOperation.responseId = fixtures.find(fixture => fixture.internalId === operation.responseId)?.id;
        }
        if (operation.url && operation.url !== '') {
            fhirOperation.url = operation.url;
        }
        if (operation.params && operation.params !== '') {
            fhirOperation.params = operation.params;
        }
        return fhirOperation;
    }

    /**
     * Transform the assert to a FHIR object
     * 
     * @param assert 
     *  
     */
    const transformAssertToFHIR = (assert: any) => {
        // Start with all the mandatory fields
        const fhirAssert = {
            id: assert.id,
            description: assert.description,
            label: assert.label,
            stopTestOnFail: assert.stopTestOnFail,
            warningOnly: assert.warningOnly,
        } as TestScriptSetupActionAssert;
        if (assert.direction && assert.direction !== '') {
            fhirAssert.direction = assert.direction;
        }
        if (assert.expression && assert.expression !== '') {
            fhirAssert.expression = assert.expression;
        }
        if (assert.responseCode && assert.responseCode !== '') {
            fhirAssert.responseCode = assert.responseCode;
        }
        if (assert.validateProfileId && assert.validateProfileId !== '') {
            fhirAssert.validateProfileId = profilesValidation.find(pv => pv.internalId === assert.validateProfileId)?.id.toString() ?? "0";
        }
        if (assert.headerField && assert.headerField !== '') {
            fhirAssert.headerField = assert.headerField;
        }
        if (assert.valueToCompare && assert.valueToCompare !== '') {
            fhirAssert.value = assert.valueToCompare;
        }
        if (assert.operator && assert.operator !== '') {
            fhirAssert.operator = assert.operator;
        }
        if (assert.requestMethod && assert.requestMethod !== '') {
            fhirAssert.requestMethod = assert.requestMethod;
        }
        if (assert.requestUrl && assert.requestUrl !== '') {
            fhirAssert.requestURL = assert.requestUrl;
        }
        if (assert.contentType && assert.contentType !== '') {
            fhirAssert.contentType = assert.contentType;
        }
        if (assert.resource && assert.resource !== '') {
            fhirAssert.resource = assert.resource;
        }
        if (assert.sourceId && assert.sourceId !== '') {
            fhirAssert.sourceId = fixtures.find(fixture => fixture.internalId === assert.sourceId)?.id
        }
        return fhirAssert;
    }

    // TESTSCRIPT CREATION

    /**
     * Check the validity of all the required fields before creating the test script
     * 
     */
    const checkValidity = () => {
        return checkGeneralInformationsValidity() && checkOriginAndDestinationValidity();
    };

    /**
      * Handle the creation of the TestScript
      * 
      */
    const handleTestScriptCreation = async () => {
        if (!checkValidity()) {
            return;
        }
        const userConfirmed = window.confirm(i18n.t('errormessage.unsaveschanges'));
        if (!userConfirmed) {
            return;
        }
        if (isNew) {
            const isUnique = await isTestScriptIdUniqueOnServer(generalInformations.id);
            if (!isUnique) {
                generalInformationsRef.current?.scrollIntoView({ behavior: 'smooth' });
                window.alert(i18n.t('errormessage.testscriptidalreadyexists', { id: generalInformations.id }));
                return;
            }
        }
        // TODO See if this is really needed ??
        const updatedOperationFormData = {
            ...operationFormData,
            setup: (operationFormData.setup ? operationFormData.setup : []).map((operation: any) => ({
                ...operation,
                type: {
                    ...operation.type,
                    code: operation.type?.code === 'custom-operation' ? operation.customCode : operation.type?.code
                }
            })),
            test: (operationFormData.test ? operationFormData.test : []).map((operation: any) => ({
                ...operation,
                type: {
                    ...operation.type,
                    code: operation.type?.code === 'custom-operation' ? operation.customCode : operation.type?.code
                }
            })),
        };
        setLoading(true);
        const transactionBundle = {
            resourceType: 'Bundle',
            type: 'transaction',
            entry: [
                ...fixtures.map((fixture) => ({
                    fullUrl: 'DocumentReference/' + fixture.id,
                    resource: {
                        resourceType: 'DocumentReference',
                        id: fixture.id,
                        status: 'current',
                        content: [
                            {
                                attachment: {
                                    contentType: 'application/fhir+json',
                                    data: handleFixtureDataEncode(fixture.resource)
                                }
                            }
                        ]
                    },
                    request: {
                        method: 'PUT',
                        url: `DocumentReference/${fixture.id}`
                    }
                })),
                {
                    fullUrl: generalInformations.id,
                    resource: {
                        resourceType: 'TestScript',
                        // General Informations
                        id: generalInformations.id,
                        meta: {
                            tag: [
                                {
                                    system: "http://isis.com/test/CodeSystem/COS-AutomatableTest",
                                    code: generalInformations.automatable
                                }
                            ]
                        },
                        url: generalInformations.url,
                        version: generalInformations.version,
                        name: generalInformations.name,
                        title: generalInformations.title,
                        status: generalInformations.status,
                        date: generalInformations.date,
                        description: generalInformations.description,
                        // Fixtures
                        fixture: fixtures.length > 0 ? fixtures.map((fixture) => ({
                            id: fixture.id,
                            resource: {
                                reference: "DocumentReference/" + fixture.id
                            },
                            // Not implemented in the back
                            autocreate: false, // fixture.autocreate,
                            autodelete: false, // fixture.autodelete,
                        })) : undefined,
                        // Origin 
                        origin: origins.length > 0 ? origins.map((origin, index) => ({
                            index: index + 1,
                            profile: {
                                system: "http://terminology.hl7.org/CodeSystem/testscript-profile-origin-types",
                                code: origin.profile
                            },
                            url: origin.url ?? undefined
                        })) : undefined,
                        // Destination
                        destination: destinations.length > 0 ? destinations.map((destination, index) => ({
                            index: index + 1,
                            profile: {
                                system: "http://terminology.hl7.org/CodeSystem/testscript-profile-destination-types",
                                code: destination.profile
                            },
                            url: destination.url ?? undefined
                        })) : undefined,
                        // Variables
                        variable: variables.length > 0 ? variables.map((variable) => {
                            const variableObject: any = {
                                id: variable.name,
                                name: variable.name,
                                sourceId: fixtures.find(fixture => fixture.internalId === variable.sourceId)?.id,
                            };
                            if (variable.expression) {
                                variableObject.expression = variable.expression;
                            }
                            if (variable.path) {
                                variableObject.path = variable.path;
                            }
                            return variableObject;
                        }) : undefined,
                        // Profiles Validation
                        profile: profilesValidation.length > 0 ? profilesValidation.map(profileValidation => profileValidation.profileUrl)
                            : undefined,
                        _profile: profilesValidation.length > 0 ? profilesValidation.map((index) => ({
                            id: index.id.toString()
                        })) : undefined,
                        // Setup
                        setup: combineFormData(updatedOperationFormData.setup, assertFormData),
                        // Test
                        test: updatedOperationFormData.test.length > 0 ? combineFormData(updatedOperationFormData.test, assertFormData) : undefined,
                        // Teardown
                        teardown: combineFormData(operationFormData.teardown, assertFormData),
                    },
                    request: {
                        method: 'PUT',
                        url: `TestScript/${generalInformations.id}`
                    }
                }
            ]
        };
        try {
            await fhirClient.transaction({
                body: transactionBundle as FhirResource & { type: "transaction" }
            });
            onTestScriptViewer(generalInformations.id);
            console.log('Created :', JSON.stringify(transactionBundle.entry.map((entry) => entry.resource), null, 2));
        } catch (error) {
            onError();
        } finally {
            setLoading(false);
        }
    };

    /////////////////////////////////////
    //          Page Content           //
    /////////////////////////////////////

    return (

        <PandoraPage
            titleKey='title.testeditor'
            loading={loading}
            needsLogin={true}
        >
            <Form
                onSubmit={(event) => {
                    setValidated(true);
                    const form = event.currentTarget;
                    if (form.checkValidity() === false) {
                        // TODO Init all mandatory fields messages just in case the form gets validated 
                        // TODO do the scroling here (most is validated by now so we can get rid of a lot of manual validation as well !)
                        checkValidity();
                        event.preventDefault();
                        event.stopPropagation();
                    } else {
                        event.preventDefault();
                        // TODO Keep here only additional validation not feasable from the form (like having at least one origin or destination)
                        handleTestScriptCreation();
                    }
                }}
                noValidate
                validated={validated}
                className="d-flex flex-column gap-4"
            >

                <div ref={generalInformationsRef}>
                    <GeneralInformationSection
                        disableId={testId !== undefined}
                        id={generalInformations.id}
                        automatable={generalInformations.automatable === "automatable"}
                        url={generalInformations.url}
                        version={generalInformations.version}
                        name={generalInformations.name}
                        title={generalInformations.title}
                        status={generalInformations.status}
                        date={generalInformations.date}
                        description={generalInformations.description}
                        setChanges={changeValueInGeneralInformation}
                        isValidated={validated}
                    />
                </div>

                {/* ORIGIN */}
                <div ref={originRef}>
                    <AbstractServerSection
                        id='origin'
                        options={originOptions}
                        requiredMessage='errormessage.originrequired'
                        servers={origins}
                        setServers={setOrigins}
                        titleKey='general.origin'
                        addKey='general.addorigin'
                        deleteMessageKey='confirmationmessage.deleteorigin'
                        isServerProfileUsed={isServerProfileUsed}
                    />
                </div>

                {/* DESTINATION */}
                <div ref={destinationRef}>
                    <AbstractServerSection
                        id='destination'
                        options={destinationOptions}
                        requiredMessage='errormessage.destinationrequired'
                        servers={destinations}
                        setServers={setDestinations}
                        titleKey='Destination'
                        addKey='general.adddestination'
                        deleteMessageKey='confirmationmessage.deletedestination'
                        isServerProfileUsed={isServerProfileUsed}
                    />
                </div>

                {/* FIXTURES */}
                <FixturesSection
                    fixtures={fixtures}
                    setFixtures={setFixtures}
                    isFixtureUsed={isFixtureUsed}
                />

                {/* VARIABLES */}
                <VariablesSection
                    variables={variables}
                    setVariables={setVariables}
                    isVariableUsed={isVariableUsed}
                    fixtures={fixtures}
                />

                {/* PROFILES  VALIDATION */}
                <ProfilesSection
                    fhirClient={fhirClient}
                    profiles={profilesValidation}
                    setProfiles={setProfilesValidation}
                    isProfileUsed={isProfileUsed}
                />

                {/* SETUP */}
                <OperationsSection
                    testStep="setup"
                    operations={operationFormData.setup ?? []}
                    asserts={assertFormData}
                    setOperationFormData={setOperationFormData}
                    fixtures={fixtures}
                    origins={origins}
                    destinations={destinations}
                    variables={variables}
                    setAssertFormData={setAssertFormData}
                    selectedProfiles={combinedProfiles}
                />

                {/* TEST */}
                <OperationsSection
                    testStep="test"
                    operations={operationFormData.test ?? []}
                    asserts={assertFormData}
                    setOperationFormData={setOperationFormData}
                    fixtures={fixtures}
                    origins={origins}
                    destinations={destinations}
                    variables={variables}
                    setAssertFormData={setAssertFormData}
                    selectedProfiles={combinedProfiles}
                />

                {/* TEARDOWN */}
                <OperationsSection
                    testStep="teardown"
                    operations={operationFormData.teardown ?? []}
                    asserts={assertFormData}
                    setOperationFormData={setOperationFormData}
                    fixtures={fixtures}
                    origins={origins}
                    destinations={destinations}
                    variables={variables}
                />

                {/* BUTTON TO CREATE A TESTSCRIPT*/}
                < Button
                    className='w-9rem'
                    variant="primary"
                    type="submit"
                >
                    {isNew ? i18n.t('button.createtestscript') : i18n.t('button.update')}
                </Button >
            </Form >
        </PandoraPage >
    );
};

export default TestEditor;
